import { useMemo } from 'react'
import { type AttributeOverride } from 'domains/products/components/view/types/attributeOverride.types'
import { useAttributeEditAttributes } from 'domains/products/components/view/utils/useAttributeEditAttributes.hooks'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import {
  getDisplayExpiryDate,
  getDisplayRemoteUser,
  getDisplayValue,
  getEntityId,
} from 'domains/products/components/view/utils/valueBasedOverrideUtils'
import { useGetValueBasedOverridesQuery, type ValueBasedOverrideResponse } from '.'

export const useGetValueBasedOverrides = (retailerId: string, productId: string) => {
  const {
    data,
    error: apiError,
    loading: apiLoading,
    refetch: apiRefetch,
  } = useGetValueBasedOverridesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      entityId: getEntityId(productId, retailerId),
    },
  })

  const { attributeNames } = useAttributeEditAttributes()
  const messages = useDomainMessages({
    unknownAttribute: 'productsDomain.view.tabs.attributeOverrides.unknownAttributeName',
    allSources: 'productsDomain.attributeOverride.sources.allSources',
  })

  const valueBasedOverrides = useMemo(
    () =>
      parseValueBasedOverrides(
        data?.valueBasedOverrideServiceGetValueBasedOverrides?.valueBasedOverride,
        attributeNames,
        messages
      ),
    [
      attributeNames,
      data?.valueBasedOverrideServiceGetValueBasedOverrides?.valueBasedOverride,
      messages,
    ]
  )

  return {
    valueBasedOverrides: valueBasedOverrides,
    apiError,
    apiLoading,
    apiRefetch,
  }
}

const parseValueBasedOverrides = (
  valueBasedOverrides: Omit<ValueBasedOverrideResponse, 'rowId' | 'entityId' | 'createdAt'>[],
  attributeNames,
  messages
) => {
  if (!valueBasedOverrides) {
    return undefined
  }

  const result: AttributeOverride[] = valueBasedOverrides.map(current => ({
    attributeName: attributeNames[current.attributeName]?.displayName || messages.unknownAttribute,
    username: getDisplayRemoteUser(current.createdByJson),
    createdWhy: current.createdWhy,
    overrideType: current.overrideType,
    expiryDate: getDisplayExpiryDate(current.expiryDate),
    overrideValue: getDisplayValue(current.overrideValue),
    sourceValue: getDisplayValue(current.sourceValue),
    sourceType: current.sourceType || messages.allSources,
    ticketUrl: current.ticketUrl,
  }))

  return result
}
